import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlayArrow as ConnectIcon,
  Stop as DisconnectIcon,
  Settings as SettingsIcon,
  Extension as ApiIcon,
  AccountTree as FlowIcon,
  Build as ToolIcon,
  ExpandMore as ExpandMoreIcon,
  CloudSync as CloudIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import mcpService from '../../services/mcpService';
import connectionTimeoutService from '../../services/connectionTimeoutService.js';
import errorRecoveryService from '../../services/errorRecoveryService.js';
import MCP_API from '../../api/mcp.api.js';

// MCP Client functionality
class MCPClient {
  constructor(config) {
    this.config = config;
    this.connected = false;
    this.websocket = null;
    this.tools = [];
    this.resources = [];
    this.prompts = [];
    this.pendingRequests = new Map();
  }

  async connect() {
    try {
      const wsUrl = `ws://${this.config.host}:${this.config.port}/api/v1/mcp/ws`;
      this.websocket = new WebSocket(wsUrl);

      return new Promise((resolve, reject) => {
        this.websocket.onopen = () => {
          this.connected = true;
          this.initialize();
          resolve(true);
        };

        this.websocket.onerror = (error) => {
          reject(error);
        };

        this.websocket.onmessage = (event) => {
          this.handleMessage(JSON.parse(event.data));
        };
      });
    } catch (error) {
      throw new Error(`Failed to connect to MCP server: ${error.message}`);
    }
  }

  async disconnect() {
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    this.connected = false;
  }

  async initialize() {
    const initRequest = {
      jsonrpc: '2.0',
      id: this.generateId(),
      method: 'initialize',
      params: {
        protocolVersion: '2024-11-05',
        capabilities: {
          tools: {},
          resources: {},
          prompts: {},
          logging: {},
        },
        clientInfo: {
          name: 'ChatUI-MCPClient',
          version: '1.0.0',
        },
      },
    };

    await this.sendRequest(initRequest);
    await this.listTools();
    await this.listResources();
  }

  async sendRequest(request) {
    if (!this.connected) {
      throw new Error('Not connected to MCP server');
    }

    return new Promise((resolve, reject) => {
      this.pendingRequests.set(request.id, { resolve, reject });
      this.websocket.send(JSON.stringify(request));

      // Timeout after 10 seconds
      setTimeout(() => {
        if (this.pendingRequests.has(request.id)) {
          this.pendingRequests.delete(request.id);
          reject(new Error('Request timeout'));
        }
      }, 10000);
    });
  }

  handleMessage(message) {
    if (message.id && this.pendingRequests.has(message.id)) {
      const { resolve, reject } = this.pendingRequests.get(message.id);
      this.pendingRequests.delete(message.id);

      if (message.error) {
        reject(new Error(message.error.message));
      } else {
        resolve(message.result);
      }
    }
  }

  async listTools() {
    const request = {
      jsonrpc: '2.0',
      id: this.generateId(),
      method: 'tools/list',
      params: {},
    };

    const result = await this.sendRequest(request);
    this.tools = result.tools || [];
    return this.tools;
  }

  async listResources() {
    const request = {
      jsonrpc: '2.0',
      id: this.generateId(),
      method: 'resources/list',
      params: {},
    };

    const result = await this.sendRequest(request);
    this.resources = result.resources || [];
    return this.resources;
  }

  async executeTool(toolName, toolArguments) {
    const request = {
      jsonrpc: '2.0',
      id: this.generateId(),
      method: 'tools/call',
      params: {
        name: toolName,
        arguments: toolArguments,
      },
    };

    const result = await this.sendRequest(request);
    return result;
  }

  generateId() {
    return Math.random().toString(36).substr(2, 9);
  }
}

function MCPServerManager({
  open,
  onClose,
  onServerSelect,
  selectedServer,
  onToolSelect,
  selectedTool,
}) {
  const [servers, setServers] = useState([]);
  const [connections, setConnections] = useState(new Map());
  const [addServerOpen, setAddServerOpen] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [newServer, setNewServer] = useState({
    name: '',
    host: 'localhost',
    port: 8001,
    enabled: true,
    description: '',
  });
  const [testingConnection, setTestingConnection] = useState(null);

  // Load servers from mcpService on mount
  useEffect(() => {
    const loadServers = async () => {
      try {
        // Ensure mcpService is initialized
        await mcpService.initialize();

        // Get servers from mcpService
        const servers = mcpService.getAllServers();
        console.log('MCPServerManager: Loaded servers from mcpService:', servers);

        // Deduplicate servers by ID to prevent React key warnings
        const uniqueServers = servers.filter((server, index, self) => index === self.findIndex((s) => s.id === server.id));

        if (uniqueServers.length !== servers.length) {
          console.log('🔧 Removed duplicate servers:', servers.length - uniqueServers.length);
        }

        setServers(uniqueServers);
      } catch (error) {
        console.error('Error loading servers in MCPServerManager:', error);
        // Fallback to empty array
        setServers([]);
      }
    };

    loadServers();
  }, []);

  // Note: Server persistence is now handled by mcpService

  const handleAddServer = async () => {
    if (!newServer.name.trim()) return;

    const server = {
      ...newServer,
      id: Date.now().toString(),
      tools: [],
      resources: [],
      lastConnected: null,
      connectionStatus: 'testing',
    };

    // Add server to mcpService
    await mcpService.addServer(server);

    // Update local state
    setServers((prev) => [...prev, server]);
    setNewServer({
      name: '',
      host: 'localhost',
      port: 8001,
      enabled: true,
      description: '',
    });
    setAddServerOpen(false);

    // Automatically test connection and load tools
    setTimeout(() => {
      handleTestConnection(server);
    }, 100);
  };

  const handleEditServer = (server) => {
    setEditingServer(server);
    setNewServer({
      name: server.name,
      host: server.host,
      port: server.port,
      enabled: server.enabled,
      description: server.description || '',
    });
    setAddServerOpen(true);
  };

  const handleUpdateServer = () => {
    if (!editingServer || !newServer.name.trim()) return;

    setServers((prev) => prev.map((server) => (server.id === editingServer.id
      ? { ...server, ...newServer }
      : server)));

    setEditingServer(null);
    setNewServer({
      name: '',
      host: 'localhost',
      port: 8001,
      enabled: true,
      description: '',
    });
    setAddServerOpen(false);
  };

  const handleDeleteServer = async (serverId) => {
    // Remove server from mcpService
    await mcpService.removeServer(serverId);

    // Update local state
    setServers((prev) => prev.filter((server) => server.id !== serverId));
    setConnections((prev) => {
      const newConnections = new Map(prev);
      newConnections.delete(serverId);
      return newConnections;
    });
  };

  const handleClearAllServers = async () => {
    if (window.confirm('Are you sure you want to clear all MCP servers? This cannot be undone.')) {
      await mcpService.clearAllServers();
      setServers([]);
      setConnections(new Map());
      console.log('🧹 All servers cleared');
    }
  };

  const handleTestConnection = async (server) => {
    setTestingConnection(server.id);

    try {
      // Use mcpService instead of inline MCPClient
      const tools = await mcpService.getAvailableTools(server.id);
      const resources = await mcpService.getAvailableResources(server.id);

      // Update server in mcpService
      await mcpService.updateServer(server.id, {
        tools,
        resources,
        lastConnected: new Date().toISOString(),
        connectionStatus: 'success',
      });

      // Update local state
      setServers((prev) => prev.map((s) => (s.id === server.id
        ? {
          ...s,
          tools,
          resources,
          lastConnected: new Date().toISOString(),
          connectionStatus: 'success',
        }
        : s)));
    } catch (error) {
      console.error('Connection test failed:', error);

      // Update server in mcpService
      await mcpService.updateServer(server.id, {
        connectionStatus: 'error',
        lastError: error.message,
      });

      // Update local state
      setServers((prev) => prev.map((s) => (s.id === server.id
        ? { ...s, connectionStatus: 'error', lastError: error.message }
        : s)));
    } finally {
      setTestingConnection(null);
    }
  };

  const handleConnect = async (server) => {
    try {
      // Use mcpService to connect
      const client = await mcpService.connectToServer(server.id);

      setConnections((prev) => new Map(prev).set(server.id, client));

      setServers((prev) => prev.map((s) => (s.id === server.id
        ? {
          ...s,
          lastConnected: new Date().toISOString(),
          connectionStatus: 'connected',
        }
        : s)));
    } catch (error) {
      console.error('Connection failed:', error);
      setServers((prev) => prev.map((s) => (s.id === server.id
        ? { ...s, connectionStatus: 'error', lastError: error.message }
        : s)));
    }
  };

  const handleDisconnect = async (server) => {
    try {
      // Use mcpService to disconnect
      await mcpService.disconnectFromServer(server.id);

      setConnections((prev) => {
        const newConnections = new Map(prev);
        newConnections.delete(server.id);
        return newConnections;
      });

      setServers((prev) => prev.map((s) => (s.id === server.id
        ? { ...s, connectionStatus: 'disconnected' }
        : s)));
    } catch (error) {
      console.error('Disconnect failed:', error);
    }
  };

  const getConnectionStatus = (server) => {
    if (connections.has(server.id)) return 'connected';
    return server.connectionStatus || 'disconnected';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'success';
      case 'error': return 'error';
      case 'success': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return <SuccessIcon />;
      case 'error': return <ErrorIcon />;
      case 'success': return <SuccessIcon />;
      default: return <WarningIcon />;
    }
  };

  if (!open) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <CloudIcon />
              <Typography variant="h6">MCP Server Manager</Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <DeleteIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3}>
            {/* Server List */}
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Connected Servers</Typography>
                <Box display="flex" gap={1}>
                  <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    onClick={() => setAddServerOpen(true)}
                  >
                    Add Server
                  </Button>
                  {servers.length > 0 && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleClearAllServers}
                      size="small"
                    >
                      Clear All
                    </Button>
                  )}
                </Box>
              </Box>

              {servers.length === 0 ? (
                <Alert severity="info">
                  No MCP servers configured. Click "Add Server" to connect to an MCP server.
                </Alert>
              ) : (
                <List>
                  {servers.map((server) => {
                    const isConnected = connections.has(server.id);
                    const status = getConnectionStatus(server);

                    return (
                      <Accordion key={server.id}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box display="flex" alignItems="center" gap={2} width="100%">
                            <Chip
                              icon={getStatusIcon(status)}
                              label={status}
                              color={getStatusColor(status)}
                              size="small"
                            />
                            <Typography variant="subtitle1" sx={{ flex: 1 }}>
                              {server.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {server.host}
                              :
                              {server.port}
                            </Typography>
                            <Box display="flex" gap={1}>
                              <Tooltip title="Test Connection">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTestConnection(server);
                                  }}
                                  disabled={testingConnection === server.id}
                                >
                                  {testingConnection === server.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <RefreshIcon />
                                  )}
                                </IconButton>
                              </Tooltip>

                              <Tooltip title={isConnected ? 'Disconnect' : 'Connect'}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isConnected) {
                                      handleDisconnect(server);
                                    } else {
                                      handleConnect(server);
                                    }
                                  }}
                                >
                                  {isConnected ? <DisconnectIcon /> : <ConnectIcon />}
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Edit Server">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditServer(server);
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete Server">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteServer(server.id);
                                  }}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails>
                          <Stack spacing={2}>
                            {server.description && (
                              <Typography variant="body2" color="text.secondary">
                                {server.description}
                              </Typography>
                            )}

                            {server.lastConnected && (
                              <Typography variant="caption" color="text.secondary">
                                Last connected:
                                {' '}
                                {new Date(server.lastConnected).toLocaleString()}
                              </Typography>
                            )}

                            {server.lastError && (
                              <Alert severity="error" size="small">
                                {server.lastError}
                              </Alert>
                            )}

                            {isConnected && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Available Tools (
                                  {server.tools?.length || 0}
                                  )
                                </Typography>
                                {server.tools && server.tools.length > 0 ? (
                                  <List dense>
                                    {server.tools.map((tool, index) => (
                                      <ListItem
                                        key={index}
                                        button
                                        selected={selectedTool === tool.name}
                                        onClick={() => onToolSelect(tool)}
                                      >
                                        <ListItemIcon>
                                          <ToolIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={tool.name}
                                          secondary={tool.description}
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No tools available
                                  </Typography>
                                )}

                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                  Available Resources (
                                  {server.resources?.length || 0}
                                  )
                                </Typography>
                                {server.resources && server.resources.length > 0 ? (
                                  <List dense>
                                    {server.resources.map((resource, index) => (
                                      <ListItem key={index}>
                                        <ListItemIcon>
                                          <ApiIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={resource.name}
                                          secondary={resource.description}
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No resources available
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </List>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Server Dialog */}
      <Dialog
        open={addServerOpen}
        onClose={() => {
          setAddServerOpen(false);
          setEditingServer(null);
          setNewServer({
            name: '',
            host: 'localhost',
            port: 8001,
            enabled: true,
            description: '',
          });
        }}
      >
        <DialogTitle>
          {editingServer ? 'Edit MCP Server' : 'Add MCP Server'}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Server Name"
              value={newServer.name}
              onChange={(e) => setNewServer((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />

            <TextField
              label="Host"
              value={newServer.host}
              onChange={(e) => setNewServer((prev) => ({ ...prev, host: e.target.value }))}
              fullWidth
              required
            />

            <TextField
              label="Port"
              type="number"
              value={newServer.port}
              onChange={(e) => setNewServer((prev) => ({ ...prev, port: parseInt(e.target.value) }))}
              fullWidth
              required
            />

            <TextField
              label="Description"
              value={newServer.description}
              onChange={(e) => setNewServer((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />

            <FormControlLabel
              control={(
                <Switch
                  checked={newServer.enabled}
                  onChange={(e) => setNewServer((prev) => ({ ...prev, enabled: e.target.checked }))}
                />
              )}
              label="Enabled"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setAddServerOpen(false)}>Cancel</Button>
          <Button
            onClick={editingServer ? handleUpdateServer : handleAddServer}
            variant="contained"
            disabled={!newServer.name.trim()}
          >
            {editingServer ? 'Update' : 'Add'}
            {' '}
            Server
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MCPServerManager;
